import React, { useState } from 'react';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import styled from 'styled-components';
import { ArrowRight, ArrowRightLeft, BadgeDollarSign, Banknote, CreditCard, LogIn, LogOut } from 'lucide-react';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  formatCurrencyValue,
} from './shared/StructuredCreditTicketLayout';
import {
  CashTransferType,
  hydrateCashTransferTransaction,
  resolveCashTransferType,
} from '../../utils/cashTransferHydration';

import { DateField } from '../shared/DateField';

// Cash Transfer-specific styled components
const TransferTypeSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const TransferTypeButton = styled.button<{ active?: boolean }>`
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid ${props => props.active ? 'rgba(34, 197, 94, 0.5)' : 'rgba(71, 85, 105, 0.45)'};
  background: ${props => props.active ? 'rgba(34, 197, 94, 0.14)' : 'rgba(15, 23, 42, 0.58)'};
  color: ${props => props.active ? '#86efac' : '#cbd5e1'};
  border-radius: 11px;
  font-weight: 850;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  
  &:hover {
    border-color: rgba(34, 197, 94, 0.5);
    color: #86efac;
    background: rgba(34, 197, 94, 0.1);
  }
`;

const TransferArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  margin: 20px 0;
`;

const TransferArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  color: #86efac;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.34);
`;

const PortfolioCard = styled.div<{ isSource?: boolean }>`
  background: ${props => props.isSource ? 'rgba(239, 68, 68, 0.09)' : 'rgba(34, 197, 94, 0.1)'};
  border: 1px solid ${props => props.isSource ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.34)'};
  border-radius: 14px;
  padding: 18px;
  flex: 1;
  min-width: 280px;
`;

const PortfolioCardTitle = styled.div<{ isSource?: boolean }>`
  font-size: 14px;
  font-weight: 850;
  color: ${props => props.isSource ? '#fca5a5' : '#86efac'};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TransferSummary = styled.div`
  background:
    radial-gradient(circle at 100% 0%, rgba(34, 197, 94, 0.14) 0%, rgba(15, 23, 42, 0) 42%),
    rgba(15, 23, 42, 0.76);
  border-radius: 14px;
  padding: 18px;
  border: 1px solid rgba(34, 197, 94, 0.28);
`;

const TransferSummaryTitle = styled.h3`
  font-size: 18px;
  font-weight: 900;
  color: #f1f5f9;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const SummaryCard = styled.div<{ highlight?: boolean }>`
  background: ${props => props.highlight ? 'rgba(34, 197, 94, 0.13)' : 'rgba(2, 6, 23, 0.34)'};
  padding: 13px;
  border-radius: 11px;
  border: 1px solid ${props => props.highlight ? 'rgba(34, 197, 94, 0.34)' : 'rgba(71, 85, 105, 0.36)'};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
`;

const SummaryValue = styled.div<{ large?: boolean; isNegative?: boolean }>`
  font-size: ${props => props.large ? '24px' : '18px'};
  font-weight: 700;
  color: ${props => props.isNegative ? '#ef4444' : '#f1f5f9'};
`;

const BalanceDisplay = styled.div`
  background: rgba(15, 23, 42, 0.6);
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 12px;
`;

const BalanceLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const BalanceValue = styled.div<{ isPositive?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.isPositive ? '#10b981' : '#f1f5f9'};
`;

const TransferVisualization = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const CashTransferTicketScreen: React.FC = () => {
  // Check for pending trade data from Discovery or other sources
  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        // Clear the sessionStorage after reading
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  // Determine initial transfer type from pending data
  const getInitialTransferType = (): CashTransferType =>
    resolveCashTransferType(pendingTrade);

  const [sourceBalance, setSourceBalance] = useState<number | null>(null);
  // Why the trade date field is empty although a date was typed (a weekend or holiday), with the next business day.
  const [tradeDateProblem, setTradeDateProblem] = useState<{ problem: string; fix: { iso: string; label: string } | null } | null>(null);
  const [destinationBalance, setDestinationBalance] = useState<number | null>(null);
  const [, setLoadingBalances] = useState(false);

  // Cash moves on its currency's calendar (ADR 0009), not the book's: a new ticket opens on a day
  // that currency settles, and settles the same day (T+0).
  const cashDate = getTodayDate(calendarsFor('CASH_TRANSFER', pendingTrade?.currency || 'USD'));

  const initialFormData = {
    transactionType: pendingTrade?.transactionType || 'TRANSFER',
    transferType: getInitialTransferType(),
    assetManager: pendingTrade?.assetManager || '',
    portfolioManager: pendingTrade?.portfolioManager || '',
    assetClass: 'Cash',
    assetSubClass: 'Cash Transfer',
    // Source portfolio (for withdrawals, this is the portfolio from the proposal)
    sourcePortfolio: pendingTrade?.transactionType === 'WITHDRAWAL' ? (pendingTrade?.portfolioName || '') : '',
    sourcePortfolioId: pendingTrade?.transactionType === 'WITHDRAWAL' ? (pendingTrade?.portfolioId || null) : null,
    // Destination portfolio (for deposits/contributions, this is the portfolio from the proposal)
    destinationPortfolio: pendingTrade?.transactionType === 'DEPOSIT' || pendingTrade?.transactionType === 'CONTRIBUTION' ? (pendingTrade?.portfolioName || '') : '',
    destinationPortfolioId: pendingTrade?.transactionType === 'DEPOSIT' || pendingTrade?.transactionType === 'CONTRIBUTION' ? (pendingTrade?.portfolioId || null) : null,
    // Amount
    amount: pendingTrade?.amount?.toString() || '',
    currency: pendingTrade?.currency || 'USD',
    // Dates
    tradeDate: pendingTrade?.tradeDate || cashDate,
    settleDate: pendingTrade?.settleDate || cashDate, // Cash settles same day
    valueDate: pendingTrade?.valueDate || cashDate,
    // Transfer details
    transferReason: pendingTrade?.transferReason || '',
    referenceNumber: pendingTrade?.referenceNumber || '',
    // For external transfers
    externalAccount: pendingTrade?.externalAccount || '',
    bankName: pendingTrade?.bankName || '',
    bankAccountNumber: pendingTrade?.bankAccountNumber || '',
    bankRoutingNumber: pendingTrade?.bankRoutingNumber || '',
    wireReference: pendingTrade?.wireReference || '',
    // Common
    submittedBy: pendingTrade?.submittedBy || '',
    executedBy: pendingTrade?.executedBy || '',
    approvedBy: pendingTrade?.approvedBy || '',
    notes: pendingTrade?.notes || '',
    rationale: pendingTrade?.rationale || ''
  };

  // Fetch portfolio cash balance
  const fetchPortfolioBalance = async (
    portfolioName: string,
    portfolios: any[],
    currency: string = 'USD'
  ): Promise<number | null> => {
    const portfolio = portfolios.find((p: any) => p.portfolioName === portfolioName);
    if (!portfolio) return null;
    
    try {
      const response = await axios.get(`/api/ibor/cash-balances/${portfolio.id}`);
      return response.data?.balances?.[currency]
        ?? response.data?.totalUSD
        ?? null;
    } catch (error) {
      console.warn('Failed to fetch portfolio balance:', error);
      return null;
    }
  };

  const validateForm = (formData: any, action: string, useAllocations: boolean = false): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const type = formData.transferType as CashTransferType;

    // Common validations
    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.tradeDate) errors.tradeDate = tradeDateProblem ? `${tradeDateProblem.problem}${tradeDateProblem.fix ? ` — use ${tradeDateProblem.fix.label}` : ''}` : 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settlement Date is required';
    
    // Amount validation
    if (!formData.amount || parseAmountShorthand(formData.amount) <= 0) {
      errors.amount = 'Amount is required and must be positive';
    }
    
    // Transfer type specific validations
    if (type === 'INTERNAL') {
      if (!formData.sourcePortfolio?.trim()) errors.sourcePortfolio = 'Source portfolio is required';
      if (!formData.destinationPortfolio?.trim()) errors.destinationPortfolio = 'Destination portfolio is required';
      if (formData.sourcePortfolio === formData.destinationPortfolio) {
        errors.destinationPortfolio = 'Source and destination portfolios must be different';
      }
    } else if (type === 'CONTRIBUTION') {
      if (!formData.destinationPortfolio?.trim()) errors.destinationPortfolio = 'Destination portfolio is required';
    } else if (type === 'WITHDRAWAL') {
      if (!formData.sourcePortfolio?.trim()) errors.sourcePortfolio = 'Source portfolio is required';
    } else if (type === 'FEE') {
      if (!formData.sourcePortfolio?.trim()) errors.sourcePortfolio = 'Source portfolio is required';
    } else if (type === 'DIVIDEND') {
      if (!formData.destinationPortfolio?.trim()) errors.destinationPortfolio = 'Destination portfolio is required';
    }
    
    // Transfer reason for certain types
    if (!formData.transferReason?.trim()) {
      errors.transferReason = 'Transfer reason is required';
    }
    if (!formData.referenceNumber?.trim()) {
      errors.referenceNumber = 'Reference Number is required for idempotent posting';
    }
    
    // Execution validations
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) {
        errors.executedBy = 'Executed By is required for execution';
      }
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any, useAllocations: boolean = false): Promise<any> => {
    const amount = parseAmountShorthand(formData.amount);
    
    if (amount <= 0) {
      throw new Error('Amount is required');
    }

    // Look up portfolios
    let sourcePortfolioId: number | null = null;
    let destinationPortfolioId: number | null = null;
    
    if (formData.sourcePortfolio) {
      const sourcePortfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.sourcePortfolio.trim());
      if (sourcePortfolio) {
        sourcePortfolioId = sourcePortfolio.id;
      }
    }
    
    if (formData.destinationPortfolio) {
      const destPortfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.destinationPortfolio.trim());
      if (destPortfolio) {
        destinationPortfolioId = destPortfolio.id;
      }
    }

    // Validate based on transfer type
    const type = formData.transferType as CashTransferType;
    if (type === 'INTERNAL' && (!sourcePortfolioId || !destinationPortfolioId)) {
      throw new Error('Both source and destination portfolios are required for internal transfers');
    }
    if (type === 'CONTRIBUTION' && !destinationPortfolioId) {
      throw new Error('Destination portfolio is required for contributions');
    }
    if (type === 'WITHDRAWAL' && !sourcePortfolioId) {
      throw new Error('Source portfolio is required for withdrawals');
    }

    const cashCusip = `${String(formData.currency || 'USD').toUpperCase()}_CASH`;
    const cashSecurityResponse = await axios.get('/api/dimensions/securities/search', {
      params: { query: cashCusip }
    });
    const cashSecurities = Array.isArray(cashSecurityResponse.data) ? cashSecurityResponse.data : [];
    const cashSecurity = cashSecurities.find((security: any) =>
      String(security.cusip || '').toUpperCase() === cashCusip);
    if (!cashSecurity?.id) {
      throw new Error(`Cash security ${cashCusip} is not configured for this tenant`);
    }

    const eventTypeByTransfer: Record<CashTransferType, string> = {
      INTERNAL: 'TRANSFER',
      CONTRIBUTION: 'CONTRIBUTION',
      WITHDRAWAL: 'WITHDRAWAL',
      FEE: 'FEE',
      DIVIDEND: 'DIVIDEND'
    };

    return {
      transactionType: 'CASH_TRANSFER',
      ticketType: 'CASH_TRANSFER',
      eventType: eventTypeByTransfer[type],
      securityId: cashSecurity.id,
      assetClass: 'Cash',
      assetSubClass: formData.transferType,
      // For backwards compatibility, use source portfolio as portfolioId
      portfolioId: sourcePortfolioId || destinationPortfolioId,
      // Cash transfer specific fields
      transferType: formData.transferType,
      sourcePortfolioId: sourcePortfolioId,
      destinationPortfolioId: destinationPortfolioId,
      amount: amount,
      currency: formData.currency,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      valueDate: formData.valueDate,
      transferReason: formData.transferReason || null,
      referenceNumber: formData.referenceNumber || null,
      sourceRef: `cash-transfer:${String(formData.referenceNumber).trim()}`,
      price: 1,
      quantity: amount,
      principal: amount,
      netAmount: amount,
      settlementAmount: amount,
      // External transfer details
      externalAccount: formData.externalAccount || null,
      bankName: formData.bankName || null,
      bankAccountNumber: formData.bankAccountNumber || null,
      bankRoutingNumber: formData.bankRoutingNumber || null,
      wireReference: formData.wireReference || null,
      // Common
      notes: formData.notes || '',
      rationale: formData.rationale || '',
      createdBy: formData.submittedBy || null,
      executedBy: formData.executedBy || null,
      approvedBy: formData.approvedBy || null,
      status: 'DRAFT'
    };
  };

  // F-BT-015: a reopened ticket carries portfolio ids (and, since 2026-09-09, names) — never lose the books.
  const loadTransactionData = (transaction: any, dimensionData?: any) => {
    const hydrated = hydrateCashTransferTransaction(transaction, getTodayDate());
    const portfolios: any[] = dimensionData?.portfolios || [];
    const nameFor = (id: any) => portfolios.find((p: any) => p?.id === id)?.portfolioName || '';
    if (!hydrated.sourcePortfolio && hydrated.sourcePortfolioId != null) {
      hydrated.sourcePortfolio = nameFor(hydrated.sourcePortfolioId);
    }
    if (!hydrated.destinationPortfolio && hydrated.destinationPortfolioId != null) {
      hydrated.destinationPortfolio = nameFor(hydrated.destinationPortfolioId);
    }
    return hydrated;
  };

  const getTransferTypeLabel = (type: CashTransferType): string => {
    switch (type) {
      case 'INTERNAL': return 'Internal Transfer';
      case 'CONTRIBUTION': return 'Capital Contribution';
      case 'WITHDRAWAL': return 'Capital Withdrawal';
      case 'FEE': return 'Fee Payment';
      case 'DIVIDEND': return 'Dividend Distribution';
      default: return type;
    }
  };

  return (
    <BaseTicketScreen
      title="Cash Transfer Entry"
      assetClass="Cash"
      hidePortfolioField={true}
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange }) => {
        const transferAmount = formData.amount ? parseAmountShorthand(formData.amount) : 0;
        const transferType = resolveCashTransferType(formData);

        return (
        <StructuredWorkspace>
          <StructuredHeroPanel
            toneName="cash"
            icon={Banknote}
            eyebrow="Cash Movement"
            title="Cash Transfer Ticket"
            description="Move cash between portfolios or external accounts with explicit source, destination, amount, value date, and approval context."
            badges={['Same-day cash', 'Balance aware', 'Transfer lifecycle']}
            signals={[
              { label: 'Transfer Type', value: getTransferTypeLabel(transferType), accent: true },
              { label: 'Amount', value: transferAmount > 0 ? formatCurrencyValue(transferAmount, formData.currency || 'USD') : '-', accent: true },
              { label: 'From', value: formData.sourcePortfolio || 'External / N/A' },
              { label: 'To', value: formData.destinationPortfolio || 'External / N/A' },
            ]}
          />
          {/* Transfer Type Selector */}
          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="cash">Transfer Type</BandEyebrow>
                <BandTitle>Cash Movement Direction</BandTitle>
                <BandSubline>Choose whether this ticket moves cash internally, contributes capital, withdraws capital, pays a fee, or records a dividend.</BandSubline>
              </BandTitleGroup>
            </BandHeader>
            <TransferTypeSelector>
              <TransferTypeButton 
                active={transferType === 'INTERNAL'} 
                onClick={() => {
                  setFormData((prev: any) => ({ ...prev, transferType: 'INTERNAL', assetSubClass: 'Internal Transfer' }));
                }}
                disabled={isFieldDisabled('transferType')}
              >
                <ArrowRightLeft size={16} />
                Internal Transfer
              </TransferTypeButton>
              <TransferTypeButton 
                active={transferType === 'CONTRIBUTION'} 
                onClick={() => {
                  setFormData((prev: any) => ({ 
                    ...prev, 
                    transferType: 'CONTRIBUTION', 
                    assetSubClass: 'Contribution',
                    sourcePortfolio: '' 
                  }));
                }}
                disabled={isFieldDisabled('transferType')}
              >
                <LogIn size={16} />
                Contribution
              </TransferTypeButton>
              <TransferTypeButton 
                active={transferType === 'WITHDRAWAL'} 
                onClick={() => {
                  setFormData((prev: any) => ({ 
                    ...prev, 
                    transferType: 'WITHDRAWAL', 
                    assetSubClass: 'Withdrawal',
                    destinationPortfolio: '' 
                  }));
                }}
                disabled={isFieldDisabled('transferType')}
              >
                <LogOut size={16} />
                Withdrawal
              </TransferTypeButton>
              <TransferTypeButton 
                active={transferType === 'FEE'} 
                onClick={() => {
                  setFormData((prev: any) => ({ ...prev, transferType: 'FEE', assetSubClass: 'Fee Payment' }));
                }}
                disabled={isFieldDisabled('transferType')}
              >
                <CreditCard size={16} />
                Fee Payment
              </TransferTypeButton>
              <TransferTypeButton 
                active={transferType === 'DIVIDEND'} 
                onClick={() => {
                  setFormData((prev: any) => ({ ...prev, transferType: 'DIVIDEND', assetSubClass: 'Dividend' }));
                }}
                disabled={isFieldDisabled('transferType')}
              >
                <BadgeDollarSign size={16} />
                Dividend
              </TransferTypeButton>
            </TransferTypeSelector>
          </StructuredBand>

          {/* Portfolio Selection with Transfer Visualization */}
          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="cash">Portfolios</BandEyebrow>
                <BandTitle>Source and Destination</BandTitle>
                <BandSubline>Select the cash source and destination books; balances load from the portfolio cash endpoint when available.</BandSubline>
              </BandTitleGroup>
            </BandHeader>
          <TransferVisualization>
            {/* Source Portfolio */}
            {(transferType === 'INTERNAL' || transferType === 'WITHDRAWAL' || transferType === 'FEE') && (
              <PortfolioCard isSource>
                <PortfolioCardTitle isSource>
                  <LogOut size={16} />
                  Source Portfolio
                </PortfolioCardTitle>
                <Select
                  value={formData.sourcePortfolio}
                  onChange={async (e) => {
                    handleInputChange('sourcePortfolio', e.target.value);
                    // Fetch balance
                    if (e.target.value) {
                      setLoadingBalances(true);
                      const balance = await fetchPortfolioBalance(
                        e.target.value, dimensionData.portfolios, formData.currency || 'USD');
                      setSourceBalance(balance);
                      setLoadingBalances(false);
                    } else {
                      setSourceBalance(null);
                    }
                  }}
                  className={errors.sourcePortfolio ? 'error' : ''}
                  disabled={isFieldDisabled('sourcePortfolio')}
                  style={{ width: '100%', marginBottom: '12px' }}
                >
                  <option value="">Select Source Portfolio</option>
                  {dimensionData.portfolios
                    .filter((p: any) => p.portfolioName !== formData.destinationPortfolio)
                    .map((p: any) => (
                      <option key={p.id} value={p.portfolioName}>
                        {p.portfolioName}
                      </option>
                    ))}
                </Select>
                {errors.sourcePortfolio && <FieldError>{errors.sourcePortfolio}</FieldError>}
                {sourceBalance !== null && (
                  <BalanceDisplay>
                    <BalanceLabel>Current Balance</BalanceLabel>
                    <BalanceValue isPositive={sourceBalance > 0}>
                      {formData.currency} {sourceBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </BalanceValue>
                  </BalanceDisplay>
                )}
              </PortfolioCard>
            )}

            {/* Arrow */}
            {transferType === 'INTERNAL' && (
              <TransferArrowContainer>
                <TransferArrow>
                  <ArrowRight size={22} />
                </TransferArrow>
              </TransferArrowContainer>
            )}

            {/* Destination Portfolio */}
            {(transferType === 'INTERNAL' || transferType === 'CONTRIBUTION' || transferType === 'DIVIDEND') && (
              <PortfolioCard>
                <PortfolioCardTitle>
                  <LogIn size={16} />
                  Destination Portfolio
                </PortfolioCardTitle>
                <Select
                  value={formData.destinationPortfolio}
                  onChange={async (e) => {
                    handleInputChange('destinationPortfolio', e.target.value);
                    // Fetch balance
                    if (e.target.value) {
                      setLoadingBalances(true);
                      const balance = await fetchPortfolioBalance(
                        e.target.value, dimensionData.portfolios, formData.currency || 'USD');
                      setDestinationBalance(balance);
                      setLoadingBalances(false);
                    } else {
                      setDestinationBalance(null);
                    }
                  }}
                  className={errors.destinationPortfolio ? 'error' : ''}
                  disabled={isFieldDisabled('destinationPortfolio')}
                  style={{ width: '100%', marginBottom: '12px' }}
                >
                  <option value="">Select Destination Portfolio</option>
                  {dimensionData.portfolios
                    .filter((p: any) => p.portfolioName !== formData.sourcePortfolio)
                    .map((p: any) => (
                      <option key={p.id} value={p.portfolioName}>
                        {p.portfolioName}
                      </option>
                    ))}
                </Select>
                {errors.destinationPortfolio && <FieldError>{errors.destinationPortfolio}</FieldError>}
                {destinationBalance !== null && (
                  <BalanceDisplay>
                    <BalanceLabel>Current Balance</BalanceLabel>
                    <BalanceValue isPositive={destinationBalance > 0}>
                      {formData.currency} {destinationBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </BalanceValue>
                  </BalanceDisplay>
                )}
              </PortfolioCard>
            )}
          </TransferVisualization>
          </StructuredBand>

          {/* Row 1: Trade Date, Settle Date, Value Date */}
          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="cash">Settlement and Ownership</BandEyebrow>
                <BandTitle>Dates, Managers, Amount, and References</BandTitle>
                <BandSubline>Set the value date, execution owner, transfer amount, currency, and optional reference details.</BandSubline>
              </BandTitleGroup>
            </BandHeader>
          <FormRow>
            <FormGroup>
              <Label htmlFor="tradeDate">Trade Date*</Label>
              <DateField
                id="tradeDate"
                value={formData.tradeDate ?? ''}
                onProblem={(problem, fix) => setTradeDateProblem(problem ? { problem, fix } : null)}
                className={errors.tradeDate ? 'error' : ''}
                disabled={isFieldDisabled('tradeDate')}
                calendars={calendarsFor('CASH_TRANSFER', formData.currency)}
                onChange={iso => {
                handleInputChange('tradeDate', iso);
                // Cash typically settles same day (T+0: the trade date is already a business day on the cash calendar)
                setFormData((prev: any) => ({
                ...prev,
                settleDate: iso,
                valueDate: iso
                }));
                }}
              />
              {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="settleDate">Settlement Date*</Label>
              <DateField
                id="settleDate"
                value={formData.settleDate ?? ''}
                className={errors.settleDate ? 'error' : ''}
                disabled={isFieldDisabled('settleDate')}
                calendars={settlementCalendars({ ticketType: 'CASH_TRANSFER', currency: formData.currency })}
                onChange={iso => handleInputChange('settleDate', iso)}
              />
              {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="valueDate">Value Date</Label>
              <DateField
                id="valueDate"
                value={formData.valueDate ?? ''}
                disabled={isFieldDisabled('valueDate')}
                onChange={iso => handleInputChange('valueDate', iso)}
              />
            </FormGroup>
          </FormRow>

          {/* Row 2: Asset Manager, Portfolio Manager, Executed By */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="assetManager">Asset Manager*</Label>
              <Select
                id="assetManager"
                value={formData.assetManager}
                onChange={(e) => handleInputChange('assetManager', e.target.value)}
                className={errors.assetManager ? 'error' : ''}
                disabled={isFieldDisabled('assetManager')}
              >
                <option value="">Select Asset Manager</option>
                {dimensionData.assetManagers.map((am: any) => (
                  <option key={am.id} value={am.assetManagerName}>
                    {am.assetManagerName}
                  </option>
                ))}
              </Select>
              {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
              <Select
                id="portfolioManager"
                value={formData.portfolioManager}
                onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                className={errors.portfolioManager ? 'error' : ''}
                disabled={isFieldDisabled('portfolioManager')}
              >
                <option value="">Select Portfolio Manager</option>
                {dimensionData.portfolioManagers.map((pm: any) => (
                  <option key={pm.id} value={pm.portfolioManagerName}>
                    {pm.portfolioManagerName}
                  </option>
                ))}
              </Select>
              {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
              <Select
                id="executedBy"
                value={formData.executedBy}
                onChange={(e) => handleInputChange('executedBy', e.target.value)}
                disabled={transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED'}
                className={errors.executedBy ? 'error' : ''}
              >
                <option value="">Select</option>
                {dimensionData.users?.map((user: any) => (
                  <option key={user.id} value={user.fullName}>
                    {user.fullName} {user.role ? `(${user.role})` : ''}
                  </option>
                ))}
              </Select>
              {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* Row 3: Amount, Currency, Reference Number */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="amount">Amount*</Label>
              <Input
                id="amount"
                type="text"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, amount: formatAmountWithCommas(parsed) }));
                  }
                }}
                placeholder="Enter amount (e.g., 1M)"
                className={errors.amount ? 'error' : ''}
                disabled={isFieldDisabled('amount')}
              />
              {errors.amount && <FieldError>{errors.amount}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="currency">Currency*</Label>
              <Select
                id="currency"
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                disabled={isFieldDisabled('currency')}
              >
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>
                    {c.currencyCode} - {c.currencyName}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="referenceNumber">Reference Number*</Label>
              <Input
                id="referenceNumber"
                type="text"
                value={formData.referenceNumber}
                onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                placeholder="Stable external or operator reference"
                className={errors.referenceNumber ? 'error' : ''}
                disabled={isFieldDisabled('referenceNumber')}
              />
              {errors.referenceNumber && <FieldError>{errors.referenceNumber}</FieldError>}
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup style={{ flex: 2 }}>
              <Label htmlFor="transferReason">Transfer Reason*</Label>
              {(transferType === 'CONTRIBUTION' || transferType === 'WITHDRAWAL') ? (
                <Select
                  id="transferReason"
                  value={formData.transferReason}
                  onChange={(e) => handleInputChange('transferReason', e.target.value)}
                  className={errors.transferReason ? 'error' : ''}
                  disabled={isFieldDisabled('transferReason')}
                >
                  <option value="">Select Reason</option>
                  {transferType === 'CONTRIBUTION' && (
                    <>
                      <option value="INITIAL_INVESTMENT">Initial Investment</option>
                      <option value="ADDITIONAL_INVESTMENT">Additional Investment</option>
                      <option value="CAPITAL_CALL">Capital Call</option>
                      <option value="REBALANCING">Rebalancing</option>
                    </>
                  )}
                  {transferType === 'WITHDRAWAL' && (
                    <>
                      <option value="DISTRIBUTION">Distribution</option>
                      <option value="REDEMPTION">Redemption</option>
                      <option value="EXPENSE_PAYMENT">Expense Payment</option>
                      <option value="TAX_PAYMENT">Tax Payment</option>
                      <option value="REBALANCING">Rebalancing</option>
                    </>
                  )}
                </Select>
              ) : (
                <Input
                  id="transferReason"
                  type="text"
                  value={formData.transferReason}
                  onChange={(e) => handleInputChange('transferReason', e.target.value)}
                  placeholder="Business purpose for this cash movement"
                  className={errors.transferReason ? 'error' : ''}
                  disabled={isFieldDisabled('transferReason')}
                />
              )}
              {errors.transferReason && <FieldError>{errors.transferReason}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="approvedBy">Approved By</Label>
              <Select
                id="approvedBy"
                value={formData.approvedBy}
                onChange={(e) => handleInputChange('approvedBy', e.target.value)}
                disabled={isFieldDisabled('approvedBy')}
              >
                <option value="">Select Approver</option>
                {dimensionData.users?.map((user: any) => (
                  <option key={user.id} value={user.fullName}>
                    {user.fullName}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>
          </StructuredBand>

          {/* Bank Details for External Transfers */}
          {(transferType === 'CONTRIBUTION' || transferType === 'WITHDRAWAL') && (
            <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="cash">Bank Details</BandEyebrow>
                    <BandTitle>External Account Instructions</BandTitle>
                    <BandSubline>Capture optional bank and wire reference details for contribution or withdrawal tickets.</BandSubline>
                  </BandTitleGroup>
                </BandHeader>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Input
                      id="bankName"
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => handleInputChange('bankName', e.target.value)}
                      placeholder="Enter bank name"
                      disabled={isFieldDisabled('bankName')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="bankAccountNumber">Account Number</Label>
                    <Input
                      id="bankAccountNumber"
                      type="text"
                      value={formData.bankAccountNumber}
                      onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                      placeholder="Enter account number"
                      disabled={isFieldDisabled('bankAccountNumber')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="bankRoutingNumber">Routing Number</Label>
                    <Input
                      id="bankRoutingNumber"
                      type="text"
                      value={formData.bankRoutingNumber}
                      onChange={(e) => handleInputChange('bankRoutingNumber', e.target.value)}
                      placeholder="Enter routing number"
                      disabled={isFieldDisabled('bankRoutingNumber')}
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="wireReference">Wire Reference</Label>
                    <Input
                      id="wireReference"
                      type="text"
                      value={formData.wireReference}
                      onChange={(e) => handleInputChange('wireReference', e.target.value)}
                      placeholder="Wire transfer reference"
                      disabled={isFieldDisabled('wireReference')}
                    />
                  </FormGroup>
                </FormRow>
            </StructuredBand>
          )}

          {/* Transfer Summary */}
          {formData.amount && (
            <TransferSummary>
              <TransferSummaryTitle>
                <Banknote size={18} />
                Transfer Summary
              </TransferSummaryTitle>
              <SummaryGrid>
                <SummaryCard highlight>
                  <SummaryLabel>Transfer Amount</SummaryLabel>
                  <SummaryValue large>
                    {formData.currency} {parseAmountShorthand(formData.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </SummaryValue>
                </SummaryCard>
                <SummaryCard>
                  <SummaryLabel>Transfer Type</SummaryLabel>
                  <SummaryValue>{getTransferTypeLabel(transferType)}</SummaryValue>
                </SummaryCard>
                {formData.sourcePortfolio && (
                  <SummaryCard>
                    <SummaryLabel>From</SummaryLabel>
                    <SummaryValue style={{ fontSize: '14px' }}>{formData.sourcePortfolio}</SummaryValue>
                    {sourceBalance !== null && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        New Balance: {formData.currency} {(sourceBalance - parseAmountShorthand(formData.amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </SummaryCard>
                )}
                {formData.destinationPortfolio && (
                  <SummaryCard>
                    <SummaryLabel>To</SummaryLabel>
                    <SummaryValue style={{ fontSize: '14px' }}>{formData.destinationPortfolio}</SummaryValue>
                    {destinationBalance !== null && (
                      <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
                        New Balance: {formData.currency} {(destinationBalance + parseAmountShorthand(formData.amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </SummaryCard>
                )}
                <SummaryCard>
                  <SummaryLabel>Value Date</SummaryLabel>
                  <SummaryValue style={{ fontSize: '14px' }}>{formData.valueDate}</SummaryValue>
                </SummaryCard>
              </SummaryGrid>
            </TransferSummary>
          )}
        </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default CashTransferTicketScreen;
